/**
 * Composable: useAdminDashboardData
 *
 * Creates and executes all widget requests in parallel.
 * Tracks per-widget loading/error/data state.
 * Supports refresh when the period changes.
 */
import { reactive, watch, nextTick } from 'vue';
import { useHCommon } from './use-page-common';
import { useHLocale } from './use-translate';
import { applyPeriodToParams, buildUrl, resolvePrevPeriod } from '../utils/dashboard-query-utils.js';
import { extractCount, normalizeHistogram, computeTrend, formatValue } from '../utils/dashboard-result-utils.js';
import { applyListFilter, resolveFilteredSources } from '../utils/dashboard-list-filters.js';

function getApiErrorMessage(response, payload) {
  const candidates = [payload?.message, payload?.error, payload?.detail, payload?.details, payload?.reason].filter(Boolean);
  const detail = candidates.length > 0 ? `: ${String(candidates[0])}` : '';
  const statusText = response?.statusText ? ` ${response.statusText}` : '';
  return `Request failed (${response.status}${statusText})${detail}`;
}

/**
 * @param {{ enabledSections: import('vue').ComputedRef, selectedPeriod: import('vue').Ref<string>, periods?: import('vue').ComputedRef }} options
 */
export function useAdminDashboardData({ enabledSections, selectedPeriod, periods }) {
  const { hHttpRequest } = useHCommon();
  const { locale } = useHLocale();

  // widgetData is a reactive map: widgetId -> { loading, error, value, trendValue, trend }
  const widgetData = reactive({});

  /**
   * Execute a single fetch for a widget item (kpi, breakdown item, or histogram).
   * Returns the raw response data.
   */
  async function fetchOne(url, params) {
    const { execute, data } = hHttpRequest({ url, params, method: 'get', immediate: false }, { throwHttpErrors: false });
    const response = await execute();
    if (!response) {
      throw new Error('Request failed');
    }
    if (!response.ok) {
      throw new Error(getApiErrorMessage(response, data.value));
    }
    return data.value;
  }

  /**
   * Run all enabled widget fetches for the current period.
   */
  async function fetchAll() {
    const period = selectedPeriod.value;
    const prevPeriod = resolvePrevPeriod(period, periods?.value);

    for (const section of enabledSections.value) {
      for (const widget of section.children || []) {
        const id = widget._id;
        if (!id) continue;

        // Keep the previous value while refetching so empty banners stay hidden
        // and banners that already have data do not flicker out and back in.
        // Reset trend: it belongs to the previous period and KPI cards already
        // show a chip skeleton when loading && trend === null.
        const prev = widgetData[id];
        widgetData[id] = {
          loading: true,
          error: null,
          value: prev?.value ?? null,
          trendValue: null,
          trend: null,
          ...(prev?.rawValue !== undefined ? { rawValue: prev.rawValue } : {}),
          ...(prev?.items ? { items: prev.items } : {}),
          ...(prev?.groups ? { groups: prev.groups } : {}),
        };

        // Kick off fetch per type (non-blocking per widget)
        if (widget.type === 'kpi' || widget.type === 'kpi-large') {
          fetchKpi(widget, id, period, prevPeriod);
          if (widget.items?.length) fetchItems(widget, id, period, prevPeriod);
        } else if (widget.type === 'histogram') {
          fetchHistogram(widget, id, period, prevPeriod);
        } else if (widget.type === 'breakdown') {
          fetchBreakdown(widget, id, section, period, prevPeriod);
        } else if (widget.type === 'filtered') {
          fetchFiltered(widget, id, period);
        }
      }
    }
  }

  async function fetchKpi(widget, id, period, prevPeriod) {
    try {
      const params = applyPeriodToParams(widget.params, period, prevPeriod);
      const url = buildUrl(widget.url, {});
      const response = await fetchOne(url, params);
      const value = extractCount(response);
      widgetData[id] = { ...widgetData[id], loading: false, value: formatValue(value, widget), rawValue: value };

      // Trend request (parallel, non-blocking for main value)
      // Skip trend for custom periods since there is no meaningful prev period
      if (widget.trend && prevPeriod) {
        fetchTrend(widget, id, period, prevPeriod, value);
      }
    } catch (err) {
      widgetData[id] = { ...widgetData[id], loading: false, error: err?.message ?? 'Request failed' };
    }
  }

  async function fetchTrend(widget, id, period, prevPeriod, currentValue) {
    try {
      const trendParams = applyPeriodToParams(widget.trend.params, period, prevPeriod);
      const trendUrl = buildUrl(widget.trend.url, {});
      const trendResponse = await fetchOne(trendUrl, trendParams);
      const trendValue = extractCount(trendResponse);
      const trend = computeTrend(currentValue, trendValue, { lowerIsBetter: widget.lowerIsBetter });
      widgetData[id] = { ...widgetData[id], trendValue, trend };
    } catch {
      // Trend failure is non-critical; keep the main value
    }
  }

  async function fetchHistogram(widget, id, period, prevPeriod) {
    try {
      const params = applyPeriodToParams(widget.params, period, prevPeriod);
      const url = buildUrl(widget.url, {});
      const response = await fetchOne(url, params);
      const buckets = normalizeHistogram(response);
      widgetData[id] = { ...widgetData[id], loading: false, value: buckets };
    } catch (err) {
      widgetData[id] = { ...widgetData[id], loading: false, error: err?.message ?? 'Request failed' };
    }
  }

  async function fetchItems(widget, id, period, prevPeriod) {
    widgetData[id].items = {};

    const promises = (widget.items || []).map(async (item) => {
      const itemId = item._id;
      if (!itemId) return;
      widgetData[id].items[itemId] = { loading: true, error: null, value: null };
      try {
        const params = applyPeriodToParams(item.params, period, prevPeriod);
        const url = buildUrl(item.url, {});
        const response = await fetchOne(url, params);
        const value = extractCount(response);
        widgetData[id].items[itemId] = { loading: false, error: null, value: formatValue(value, item), rawValue: value };
      } catch (err) {
        widgetData[id].items[itemId] = { loading: false, error: err?.message ?? 'Request failed', value: null };
      }
    });

    await Promise.allSettled(promises);
  }

  async function fetchBreakdown(widget, id, section, period, prevPeriod) {
    widgetData[id] = { ...widgetData[id], loading: true };
    await fetchItems(widget, id, period, prevPeriod);
    widgetData[id] = { ...widgetData[id], loading: false };
  }

  async function fetchFiltered(widget, id, period) {
    const sources = resolveFilteredSources(widget);

    if (sources.length === 0) {
      widgetData[id] = { ...widgetData[id], loading: false, error: null, groups: [] };
      return;
    }

    widgetData[id] = {
      ...widgetData[id],
      loading: true,
      error: null,
      groups: [],
    };

    const results = await Promise.allSettled(
      sources.map(async (source) => {
        const params = applyPeriodToParams(source.params, period, null);
        const url = buildUrl(source.url, {});
        const response = await fetchOne(url, params);
        const items = applyListFilter(source.filter, response, {
          locale: locale.value,
          sourceField: source.sourceField,
        });

        return {
          label: source.label,
          items,
          error: null,
          loading: false,
          rawResponse: response,
        };
      }),
    );

    const groups = results.map((result, index) => {
      if (result.status === 'fulfilled') {
        return result.value;
      }

      return {
        label: sources[index].label,
        items: [],
        error: result.reason?.message ?? 'Request failed',
        loading: false,
        rawResponse: null,
      };
    });

    const hasItems = groups.some((group) => group.items.length > 0);
    const allFailed = groups.every((group) => group.error);

    widgetData[id] = {
      ...widgetData[id],
      loading: false,
      error: allFailed && !hasItems ? (groups.find((group) => group.error)?.error ?? 'Request failed') : null,
      groups,
    };
  }

  function reapplyFilteredWidgets() {
    for (const section of enabledSections.value) {
      for (const widget of section.children || []) {
        if (widget.type !== 'filtered') continue;

        const id = widget._id;
        const state = widgetData[id];
        if (!id || !state?.groups) continue;

        const sources = resolveFilteredSources(widget);

        state.groups = state.groups.map((group, index) => {
          const source = sources[index];
          if (!source || !group.rawResponse) return group;

          return {
            ...group,
            items: applyListFilter(source.filter, group.rawResponse, {
              locale: locale.value,
              sourceField: source.sourceField,
            }),
          };
        });
      }
    }
  }

  // Trigger data fetching when sections become available and when period changes.
  // This keeps initial load and refresh behavior deterministic in both app and E2E runs.
  watch(
    [enabledSections, selectedPeriod],
    ([sections]) => {
      if (sections?.length > 0) {
        nextTick(() => fetchAll());
      }
    },
    { immediate: true },
  );

  watch(locale, () => {
    if (enabledSections.value.length > 0) {
      reapplyFilteredWidgets();
    }
  });

  function refresh() {
    fetchAll();
  }

  return { widgetData, refresh };
}
